// analytics.js — Barakah Budget minimal privacy-first event tracking
// Rules:
// - localStorage only, no backend, no third party
// - No PII: no names, amounts, categories, transactions
// - One-time events deduplicated via guard keys
// - Export only analytics-safe keys

const EVENTS_KEY = "bb_events";
const MAX_EVENTS = 300;

// ── CORE TRACK ────────────────────────────────────────────────
// Always appends. Use trackOnce for one-time events.
export function track(event) {
  try {
    const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");
    events.push({ e: event, t: Date.now() });
    if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
    localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
  } catch {}
}

// ── ONCE GUARD ────────────────────────────────────────────────
// Fires event only if it has never been fired before.
// Uses bb_tracked_<event> as guard key.
export function trackOnce(event) {
  try {
    // Check bb_events itself — so clearing bb_events also resets once-guards
    const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");
    if (events.some(e => e.e === event)) return;
    track(event);
  } catch {}
}

// ── WEEKLY REVIEW SHOWN ───────────────────────────────────────
// Fires once per review cycle (identified by week start date).
// Prevents firing on every re-render.
export function trackReviewShown() {
  try {
    const cycleId = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const lastCycle = localStorage.getItem("bb_review_shown_cycle");
    if (lastCycle === cycleId) return;
    localStorage.setItem("bb_review_shown_cycle", cycleId);
    track("weekly_review_shown");
  } catch {}
}

// ── DAY 7 RETURN ─────────────────────────────────────────────
// Fires once when user returns 6–10 days after onboarding.
export function checkDay7Return() {
  try {
    const doneAt = parseInt(localStorage.getItem("bb_onboard_done_at") || "0");
    if (!doneAt) return;
    const daysSince = (Date.now() - doneAt) / (1000 * 60 * 60 * 24);
    if (daysSince >= 6 && daysSince <= 10) {
      trackOnce("day_7_return"); // trackOnce checks bb_events — fires only once
    }
  } catch {}
}

// ── ANALYTICS EXPORT ─────────────────────────────────────────
// Returns ONLY analytics-safe data. Never touches transactions,
// budgets, names, income, categories, or any financial data.
export function getAnalyticsExport() {
  const SAFE_KEYS = [
    "bb_events",
    "bb_feedback",
    "bb_onboard_done_at",
    "bb_last_review",
    "bb_review_shown_cycle",
  ];

  const data = {};
  SAFE_KEYS.forEach(key => {
    const val = localStorage.getItem(key);
    if (val !== null) data[key] = val;
  });

  // Derive summary from events
  const events = JSON.parse(data.bb_events || "[]");
  const summary = {};
  events.forEach(({ e }) => { summary[e] = (summary[e] || 0) + 1; });

  // Include actual feedback messages — tester-written text only, no finance data
  let feedback_messages = [];
  try {
    feedback_messages = JSON.parse(localStorage.getItem("bb_feedback") || "[]");
  } catch {}

  return {
    exported_at: new Date().toISOString(),
    app_version: process.env.REACT_APP_VERSION || "dev",
    event_summary: summary,
    event_count: events.length,
    raw_events: events,
    feedback_messages, // { ts, useful, confused, comeback }
    meta: {
      onboard_done_at: data.bb_onboard_done_at || null,
      last_review: data.bb_last_review || null,
      day7_returned: events.some(e => e.e === "day_7_return"),
    },
  };
}

// ── COPY TO CLIPBOARD ────────────────────────────────────────
export async function copyAnalyticsToClipboard() {
  try {
    const payload = getAnalyticsExport();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    return true;
  } catch {
    return false;
  }
}